import { themeVariables } from "../../constants/tokens.js";
import { inputs } from "./inputs.js";
import { markdownReplacements } from "./markdown.js";
const tables = {
  MuiTableCell: {
    styleOverrides: {
      head: {
        backgroundColor: themeVariables.color.table.head.background,
        color: themeVariables.color.table.head.text,
        fontFamily: themeVariables.fonts.family.headline,
        fontSize: themeVariables.fonts.size.xs,
        fontWeight: themeVariables.fonts.weight.medium,
        lineHeight: themeVariables.fonts.lineHeight["leading-4"],
        letterSpacing: themeVariables.fonts.letterSpacing.wide,
        borderBottom: `${themeVariables.sizes.borderWidth[1]} solid ${themeVariables.color.table.head.border}`
      },
      body: {
        backgroundColor: themeVariables.color.table.body.background,
        borderBottom: `${themeVariables.sizes.borderWidth[1]} solid ${themeVariables.color.table.body.border}`,
        color: themeVariables.color.table.body.text,
        fontFamily: themeVariables.fonts.family.body,
        fontSize: themeVariables.fonts.size.sm,
        fontWeight: themeVariables.fonts.weight.medium,
        lineHeight: themeVariables.fonts.lineHeight["leading-5"],
        letterSpacing: themeVariables.fonts.letterSpacing.wide
      },
      footer: {
        backgroundColor: themeVariables.color.table.footer.background,
        color: themeVariables.color.table.footer.text,
        fontFamily: themeVariables.fonts.family.headline,
        fontSize: themeVariables.fonts.size.xs,
        fontWeight: themeVariables.fonts.weight.medium,
        lineHeight: themeVariables.fonts.lineHeight["leading-4"],
        letterSpacing: themeVariables.fonts.letterSpacing.wide
      }
    }
  }
};
const calendar = {
  MuiDateRangePickerDay: {
    styleOverrides: {
      root: {},
      rangeIntervalPreview: {
        border: "none"
      },
      rangeIntervalDayPreview: {
        border: "none",
        backgroundColor: themeVariables.color.surface["brand-light"]
      },
      rangeIntervalDayHighlight: {
        border: "none",
        backgroundColor: themeVariables.color.surface["brand-light"]
      },
      rangeIntervalDayHighlightEnd: {
        border: "none",
        backgroundColor: themeVariables.color.surface["brand-light"],
        "& .MuiButtonBase-root": {
          backgroundColor: themeVariables.color.surface.brand,
          color: themeVariables.color.text.inverted,
          border: "none",
          borderRadius: themeVariables.sizes.radius.full
        }
      },
      rangeIntervalDayHighlightStart: {
        border: "none",
        backgroundColor: themeVariables.color.surface["brand-light"],
        "& .MuiButtonBase-root": {
          backgroundColor: themeVariables.color.surface.brand,
          color: themeVariables.color.text.inverted,
          border: "none",
          borderRadius: themeVariables.sizes.radius.full
        }
      },
      day: {
        border: "none",
        "&.Mui-selected": {
          // backgroundColor: themeVariables.color.surface['brand-light'],
        },
        "&.Mui-selected:hover": {
          border: "none",
          backgroundColor: themeVariables.color.surface.brand,
          color: themeVariables.color.text.inverted
        }
      },
      dayOutsideMonth: {
        border: "none",
        color: themeVariables.color.text.disabled
      }
    }
  },
  MuiPickersDay: {
    styleOverrides: {
      root: {
        border: "none",
        "&.Mui-selected": {
          backgroundColor: themeVariables.color.surface.brand,
          color: themeVariables.color.text.inverted
        },
        "&.Mui-selected:focus": {
          backgroundColor: themeVariables.color.surface.brand,
          color: themeVariables.color.text.inverted
        },
        "&.Mui-selected:hover": {
          border: "none",
          backgroundColor: themeVariables.color.surface.brand,
          color: themeVariables.color.text.inverted
        }
      },
      dayOutsideMonth: {
        border: "none",
        color: themeVariables.color.text.disabled
      }
    }
  }
};
const components = {
  ...inputs,
  ...tables,
  MuiSkeleton: {
    styleOverrides: {
      root: {
        backgroundColor: themeVariables.color.icon.disabled
      }
    }
  },
  // A spinner takes `palette.primary.main` by default, which is white and so vanishes. Only the
  // primary colour is pinned, so `color='inherit'` spinners inside buttons keep the button's text colour.
  MuiCircularProgress: {
    styleOverrides: {
      colorPrimary: {
        color: themeVariables.color.text.primary
      }
    }
  },
  // MUI paints an icon button's hover circle with `background-color: var(--IconButton-hoverBg)`.
  // The design system has no such circle, so the variable is emptied rather than the rule
  // overridden: buttons that do want a hover keep setting `&:hover` themselves.
  MuiIconButton: {
    styleOverrides: {
      root: {
        "--IconButton-hoverBg": "transparent"
      }
    }
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundColor: themeVariables.color.surface.primary
      }
    }
  },
  // MUI X styles the tooltip paper with `var(--mui-palette-background-paper)`. The tokens are
  // set explicitly so the tooltip also works under the theme `Provider`, which has no CSS
  // variables, and to add the border and radius the design system asks for.
  MuiChartsTooltip: {
    styleOverrides: {
      paper: {
        backgroundColor: themeVariables.color.surface.primary,
        color: themeVariables.color.text.primary,
        border: `${themeVariables.sizes.borderWidth[1]} solid ${themeVariables.color.border.secondary}`,
        borderRadius: themeVariables.sizes.radius.md
      }
    }
  },
  MuiGauge: {
    styleOverrides: {
      referenceArc: () => ({
        // Styles for the reference arc (background part)
        fill: themeVariables.color.surface.tertiary
      })
    }
  },
  MuiTabs: {
    styleOverrides: {
      indicator: {
        backgroundColor: themeVariables.color.surface.brand,
        height: themeVariables.sizes.spacing["02"]
      },
      list: {
        gap: themeVariables.sizes.spacing["07"]
      }
    }
  },
  MuiTab: {
    defaultProps: {
      disableRipple: true
    },
    styleOverrides: {
      root: {
        color: themeVariables.color.text.secondary,
        padding: 0,
        paddingBottom: themeVariables.sizes.spacing["04"],
        textTransform: "none",
        minWidth: 0,
        "&.Mui-selected": {
          color: themeVariables.color.text.secondary
        }
      }
    }
  },
  MuiChip: {
    styleOverrides: {
      root: {
        backgroundColor: themeVariables.color.surface.tertiary,
        borderColor: "transparent",
        // MUI fades the delete icon to 26% of the text colour and darkens it to 40% on hover.
        // It is a solid black x here, with no hover. Nested under the root because MUI's own
        // rules are descendant selectors; a `deleteIcon` slot override would lose to them.
        ["& .MuiChip-deleteIcon"]: {
          color: themeVariables.color.text.primary,
          ["&:hover"]: {
            color: themeVariables.color.text.primary
          }
        }
      },
      label: {
        color: themeVariables.color.text.primary
      }
    }
  },
  MuiMenu: {
    styleOverrides: {
      paper: {
        backgroundColor: themeVariables.color.surface.primary,
        border: `1px solid ${themeVariables.color.border.secondary}`,
        borderRadius: themeVariables.sizes.radius.md,
        padding: themeVariables.sizes.spacing["04"],
        boxShadow: "0px 6px 8px -2px rgba(36, 5, 5, 0.12), 0px 2px 4px -2px rgba(36, 5, 5, 0.06)",
        minWidth: "200px"
      }
    }
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        height: "40px",
        borderRadius: themeVariables.sizes.radius.sm,
        padding: "0 16px 0 14px",
        fontFamily: themeVariables.fonts.family.headline,
        fontWeight: themeVariables.fonts.weight.medium,
        fontSize: themeVariables.fonts.size.sm,
        lineHeight: themeVariables.fonts.lineHeight["leading-5"],
        letterSpacing: themeVariables.fonts.letterSpacing.wide,
        color: themeVariables.color.text.secondary,
        "&:hover": {
          backgroundColor: themeVariables.color.surface.secondary
        },
        "& .MuiListItemIcon-root": {
          minWidth: "24px",
          color: themeVariables.color.text.secondary
        },
        "&.Mui-disabled": {
          opacity: 0.4
        },
        // '&:focus, &.Mui-Focused, &.Mui-focusVisible': {
        //   outlineOffset: '2px',
        //   outline: `2px solid ${themeVariables.color.border.focus}`,
        // },
        '[role="listbox"] &': {
          padding: "0 16px",
          "&:hover": {
            backgroundColor: themeVariables.color.surface.hover,
            color: themeVariables.color.text.brand
          },
          "&.Mui-selected": {
            backgroundColor: themeVariables.color.surface["brand-dark"],
            color: themeVariables.color.text.inverted,
            "&:hover": {
              backgroundColor: themeVariables.color.surface["brand-dark"],
              color: themeVariables.color.text.brand
            }
          }
        }
      }
    }
  },
  ...calendar
};
export {
  components as default,
  markdownReplacements
};
